import { FCX } from "@datassential/platform-ui-lib";
import { useLayoutEffect, useRef } from "react";
import cn from "classnames";
import InfoBlock from "@components/new/InfoBlock/InfoBlock";
import { SalesStrategySection } from "@apiHooks/useSalesStrategy";
import {
    addClassesToHtmlTags,
    formatReferenceLinks,
    getStrategyContentSections,
    toTitleCase,
} from "../Strategy.helpers";
import {
    CONTACT_INFO_SECTION_ID,
    FULL_WIDTH_SECTION_IDS,
    REFERENCES_SECTION_ID,
    SSB_SELLING_FOCUS_TITLE,
} from "../Strategy.constants";
import "./StrategyContent.styles.scss";

interface StrategyContentProps {
    sections: SalesStrategySection[];
    isExpanded: boolean;
    userInput: string | null;
}

const CONTACT_TABLE_CLASSES = {
    table: "StrategyContent__table",
    tr: "StrategyContent__table-row",
    th: "StrategyContent__table-th",
    td: "StrategyContent__table-td",
};

function renderSectionPanel(section: SalesStrategySection) {
    const rawHtml = section.html ?? '';
    const html = section.id === CONTACT_INFO_SECTION_ID
        ? addClassesToHtmlTags(rawHtml, CONTACT_TABLE_CLASSES)
        : section.id === REFERENCES_SECTION_ID
            ? formatReferenceLinks(rawHtml)
            : rawHtml;

    const sectionHtmlClasses = cn(
        "StrategyContent__section-html",
        section.id === REFERENCES_SECTION_ID && "StrategyContent__section-html--references",
    );

    return (
        <InfoBlock key={section.id} className="StrategyContent__panel" title={toTitleCase(section.title ?? '')}>
            <div className={sectionHtmlClasses} dangerouslySetInnerHTML={{ __html: html }} />
        </InfoBlock>
    );
}

function renderRows(sections: SalesStrategySection[]) {
    const rows: JSX.Element[] = [];
    let index = 0;

    while (index < sections.length) {
        const section = sections[index];
        const isFullWidth = FULL_WIDTH_SECTION_IDS.includes(section.id ?? '');

        if (isFullWidth) {
            const rowClasses = cn(
                "StrategyContent__row",
                "StrategyContent__row--full",
                section.id === REFERENCES_SECTION_ID && "StrategyContent__row--references",
            );

            rows.push(
                <div className={rowClasses} key={section.id}>
                    {renderSectionPanel(section)}
                </div>
            );
            index += 1;
            continue;
        }

        const next = sections[index + 1];
        const canPairWithNext = !!next && !FULL_WIDTH_SECTION_IDS.includes(next.id ?? '');

        rows.push(
            <div className="StrategyContent__row" key={section.id}>
                {renderSectionPanel(section)}
                {canPairWithNext && renderSectionPanel(next)}
            </div>
        );
        index += canPairWithNext ? 2 : 1;
    }

    return rows;
}

const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]';

const StrategyContent: FCX<StrategyContentProps> = ({ sections, isExpanded, userInput, className }) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const { overviewSections, detailSections } = getStrategyContentSections(sections);

    useLayoutEffect(() => {
        const rootEl = rootRef.current;
        if (!rootEl || isExpanded) return undefined;

        const focusables = rootEl.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        focusables.forEach(el => el.setAttribute('tabindex', '-1'));

        return () => focusables.forEach(el => el.removeAttribute('tabindex'));
    }, [isExpanded, sections]);

    return (
        <div
            ref={rootRef}
            className={cn("StrategyContent", isExpanded && "StrategyContent--expanded", className)}
            aria-hidden={!isExpanded}
        >
            <div className="StrategyContent__inner">
                {userInput && userInput.trim() !== '' && (
                    <InfoBlock className="StrategyContent__panel" title={SSB_SELLING_FOCUS_TITLE}>
                        <p className="StrategyContent__section-html">{userInput}</p>
                    </InfoBlock>
                )}

                {renderRows(overviewSections)}
                {renderRows(detailSections)}
            </div>
        </div>
    );
};

export default StrategyContent;
